import { useEffect, useState, type FormEvent } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { EmailTokenInfo, SessionUser } from '@jupi/shared'
import { ApiError, api, useAuth } from '@jupi/ui'
import { AuthCard } from '@jupi/ui'
import { NewPasswordFields } from '../components/NewPasswordFields'
import { problemCode, problemReason, rememberMethod, takeFragmentToken } from '@jupi/ui'

// Where a "Forgot password?" email and an administrator's password invite
// both land: /reset-password#token=... The link is checked first (nothing
// is spent), then the new password spends it and signs the member in.
export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { acceptSession } = useAuth()
  const [token] = useState(takeFragmentToken)
  const [info, setInfo] = useState<EmailTokenInfo | null>(null)
  const [invalid, setInvalid] = useState(!token)
  const [password, setPassword] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return
    api
      .post<EmailTokenInfo>('/api/auth/email-token/inspect', { token })
      .then((found) => (found.purpose === 'magic_link' ? setInvalid(true) : setInfo(found)))
      .catch(() => setInvalid(true))
  }, [token])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!token || !password) return
    setBusy(true)
    setError(null)
    try {
      const { user } = await api.post<{ user: SessionUser }>('/api/auth/password/reset', { token, newPassword: password })
      rememberMethod('password')
      acceptSession(user)
      navigate('/', { replace: true })
    } catch (err) {
      if (problemCode(err) === 'weak_password') {
        setError(t(`auth.passwordProblem_${problemReason(err) ?? 'tooShort'}`))
      } else if (err instanceof ApiError && err.status === 400) {
        setInvalid(true)
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t('auth.accountInactive'))
      } else {
        setError(t('common.error'))
      }
    } finally {
      setBusy(false)
    }
  }

  const title = info?.purpose === 'invite' ? t('auth.setTitle') : t('auth.resetTitle')

  if (invalid) {
    return (
      <AuthCard title={t('auth.resetTitle')} subtitle={t('app.intra')}>
        <Stack spacing={2}>
          <Alert severity="warning">{t('auth.linkInvalid')}</Alert>
          <Button component={RouterLink} to="/forgot-password" variant="contained" fullWidth>
            {t('auth.requestNewLink')}
          </Button>
          <Button component={RouterLink} to="/login" fullWidth>
            {t('auth.backToSignIn')}
          </Button>
        </Stack>
      </AuthCard>
    )
  }

  if (!info) {
    return (
      <AuthCard title={t('auth.resetTitle')} subtitle={t('auth.checkingLink')}>
        <Stack sx={{ alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={title} subtitle={t('auth.resetFor', { email: info.email })}>
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          {/* Lets a password manager file the new password under the right account. */}
          <input type="email" name="username" autoComplete="username" value={info.email} readOnly hidden />
          {error ? <Alert severity="error">{error}</Alert> : null}
          <NewPasswordFields email={info.email} onChange={setPassword} />
          <Button type="submit" variant="contained" size="large" fullWidth disabled={busy || !password}>
            {t('auth.savePassword')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {info.displayName}
          </Typography>
        </Stack>
      </form>
    </AuthCard>
  )
}
